import { FileResponse } from "../lib/api";

/**
 * NetCDF metadata view.
 *
 * In M1 we display dims + variables + attrs as a structured tree. M2 adds
 * "plot this variable" buttons that call the Claude `plot` tool.
 */
export default function NetCDFViewer(props: {
  payload: Extract<FileResponse, { mode: "netcdf" }>;
}) {
  const { dimensions, variables, attrs, file_size_bytes } = props.payload;
  return (
    <div className="h-full overflow-auto px-6 py-4 text-sm">
      <h2 className="mb-2 text-base font-semibold">NetCDF metadata</h2>
      <p className="mb-4 text-xs text-ink-500">
        {Math.round(file_size_bytes / 1024)} KiB · {variables.length} variables ·{" "}
        {Object.keys(dimensions).length} dimensions
      </p>

      <section className="mb-4">
        <h3 className="mb-1 font-medium text-ink-700">Dimensions</h3>
        <table className="text-xs">
          <tbody>
            {Object.entries(dimensions).map(([name, size]) => (
              <tr key={name}>
                <td className="pr-3 font-mono">{name}</td>
                <td className="text-ink-700">{size}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="mb-4">
        <h3 className="mb-1 font-medium text-ink-700">Variables</h3>
        <div className="space-y-2">
          {variables.map((v) => (
            <div key={v.name} className="rounded border border-ink-200 p-2">
              <div className="font-mono text-xs">
                {v.name}
                <span className="ml-2 text-ink-500">
                  {v.dtype} ({v.shape.join(" × ")})
                </span>
              </div>
              {Object.keys(v.attrs).length > 0 && (
                <pre className="mt-1 whitespace-pre-wrap text-xs text-ink-500">
                  {Object.entries(v.attrs)
                    .map(([k, val]) => `${k}: ${stringify(val)}`)
                    .join("\n")}
                </pre>
              )}
            </div>
          ))}
        </div>
      </section>

      {Object.keys(attrs).length > 0 && (
        <section>
          <h3 className="mb-1 font-medium text-ink-700">Global attributes</h3>
          <pre className="whitespace-pre-wrap text-xs text-ink-500">
            {Object.entries(attrs)
              .map(([k, v]) => `${k}: ${stringify(v)}`)
              .join("\n")}
          </pre>
        </section>
      )}
    </div>
  );
}

function stringify(v: unknown): string {
  if (typeof v === "string") return v;
  return JSON.stringify(v);
}
